import React, { useMemo, useState } from "react";
import { getInvoiceList } from "../../utils/urls";
import ServerPaginationDataGridWithReactQuery from "../../components/common/CustomDataGrid/ServerPaginationDataGridWithReactQuery";
import CustomChip from "../../components/common/CustomChip";
import CustomButton from "../../components/common/CustomButton/CustomButton";
import { Eye, ChevronLeft, ChevronRight } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import AdvisorConditionsPage from "../common/AdvisorConditionsPage";

const MONTHS = [
  "January","February","March","April","May","June",
  "July","August","September","October","November","December",
];

function MonthYearPicker({ selectedMonth, selectedYear, onChange }) {
  const [open, setOpen] = useState(false);
  const [viewYear, setViewYear] = useState(selectedYear);
  const nowYear = new Date().getFullYear();
  const nowMonth = new Date().getMonth();

  const handleSelect = (monthIdx) => {
    onChange(monthIdx, viewYear);
    setOpen(false);
  };

  return (
    <div className="relative">
      <button
        onClick={() => setOpen((o) => !o)}
        className="flex items-center gap-2 px-4 py-2 border border-[#E2E8F0] rounded-xl bg-white text-[0.875rem] font-semibold text-[#0F172A] hover:border-[#0184FF] transition-colors shadow-sm"
      >
        <span>{MONTHS[selectedMonth]} {selectedYear}</span>
        <ChevronRight
          size={16}
          className={`text-[#64748B] transition-transform duration-200 ${open ? "rotate-90" : ""}`}
        />
      </button>

      {open && (
        <>
          <div className="fixed inset-0 z-40" onClick={() => setOpen(false)} />
          <div className="absolute right-0 top-full mt-2 z-50 bg-white border border-[#E2E8F0] rounded-2xl shadow-lg p-4 w-72">
            <div className="flex items-center justify-between mb-3">
              <button
                onClick={() => setViewYear((y) => y - 1)}
                className="p-1.5 rounded-lg hover:bg-[#F1F5F9] transition-colors"
              >
                <ChevronLeft size={16} className="text-[#475569]" />
              </button>
              <p className="text-[0.9375rem] font-bold text-[#0F172A]">{viewYear}</p>
              <button
                onClick={() => setViewYear((y) => y + 1)}
                disabled={viewYear >= nowYear}
                className="p-1.5 rounded-lg hover:bg-[#F1F5F9] transition-colors disabled:opacity-30"
              >
                <ChevronRight size={16} className="text-[#475569]" />
              </button>
            </div>
            <div className="grid grid-cols-3 gap-1.5">
              {MONTHS.map((m, i) => {
                const isFuture = viewYear === nowYear && i > nowMonth;
                const isSelected = i === selectedMonth && viewYear === selectedYear;
                return (
                  <button
                    key={m}
                    disabled={isFuture}
                    onClick={() => handleSelect(i)}
                    className={`py-2 text-[0.8rem] font-semibold rounded-lg transition-colors ${
                      isSelected
                        ? "bg-[#0184FF] text-white"
                        : isFuture
                        ? "text-[#CBD5E1] cursor-not-allowed"
                        : "hover:bg-[#EAF4FF] text-[#0F172A]"
                    }`}
                  >
                    {m.slice(0, 3)}
                  </button>
                );
              })}
            </div>
          </div>
        </>
      )}
    </div>
  );
}

const InvoiceListTable = () => {
  const loggedInUser = useSelector((state) => state.auth);
  const navigate = useNavigate();
  const today = new Date();

  const [selectedMonth, setSelectedMonth] = useState(today.getMonth());
  const [selectedYear, setSelectedYear] = useState(today.getFullYear());

  const { start_date, end_date } = useMemo(() => {
    const first = new Date(selectedYear, selectedMonth, 1);
    const last = new Date(selectedYear, selectedMonth + 1, 0);
    return {
      start_date: first.toISOString().split("T")[0],
      end_date: last.toISOString().split("T")[0],
    };
  }, [selectedMonth, selectedYear]);

  const formatDate = (dateStr) => {
    if (!dateStr) return "-";
    const date = new Date(dateStr);
    if (isNaN(date)) return "-";
    return `${date.getDate().toString().padStart(2, "0")}-${date.toLocaleString("default", { month: "short" })}-${date.getFullYear()}`;
  };

  const getChipVariant = (status) => {
    switch (status) {
      case "Accepted": return "success";
      case "Rejected": return "error";
      default: return "default";
    }
  };

  const handleNavigate = (invoice_display_id, invoice_id, advisor_invoice_status) => {
    navigate(`/invoices/details/${invoice_display_id}`, {
      state: { invoice_id, advisor_invoice_status },
    });
  };

  const columns = useMemo(
    () => [
      { field: "invoice_display_id", headerName: "Invoice Number", flex: 1 },
      { field: "generated_amount", headerName: "Payment Amount", flex: 1 },
      { field: "created_at", headerName: "Invoice Date", flex: 1 },
      {
        field: "advisor_invoice_status",
        headerName: "Status",
        flex: 1,
        renderCell: (params) => {
          const status = params.advisor_invoice_status || "Unknown";
          return <CustomChip label={status} variant={getChipVariant(status)} />;
        },
      },
      {
        field: "action",
        headerName: "Action",
        flex: 1,
        renderCell: (params) => (
          <CustomButton
            label="View"
            variant="text"
            size="sm"
            type="button"
            startIcon={<Eye size={16} />}
            onClick={() =>
              handleNavigate(params.invoice_display_id, params.invoice_id, params.advisor_invoice_status)
            }
          />
        ),
      },
    ],
    [selectedMonth, selectedYear]
  );

  const dataMapper = (data = []) =>
    data.map((item, index) => ({
      id: item.id || index,
      invoice_display_id: item.invoice_display_id || "-",
      generated_amount: item.generated_amount || "-",
      created_at: formatDate(item.created_at),
      advisor_invoice_status: item.advisor_invoice_status || "-",
      invoice_id: item.invoice_id,
    }));

  return (
    <div className="page-container flex-1">
      <div className="my-3 flex justify-between items-center gap-4 flex-wrap">
        <h1 className="text-xl font-bold text-blue-500">Invoices</h1>
        <MonthYearPicker
          selectedMonth={selectedMonth}
          selectedYear={selectedYear}
          onChange={(m, y) => { setSelectedMonth(m); setSelectedYear(y); }}
        />
      </div>

      {loggedInUser?.isSkipLogin || !loggedInUser?.isAuthenticated ? (
        <AdvisorConditionsPage type="Skip/NotLogin" />
      ) : (
        <ServerPaginationDataGridWithReactQuery
          key={`${selectedMonth}-${selectedYear}`}
          url={getInvoiceList}
          requestParameter={{ start_date, end_date }}
          limit={10}
          columns={columns}
          isServerPagination={true}
          keyId="id"
          dataMapper={dataMapper}
        />
      )}
    </div>
  );
};

export default InvoiceListTable;
